import { IonModal } from '@ionic/react';
import { BellOff, UserX, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProfileStore } from '../../store/useProfileStore';
import { useSnoozeChef } from '../../hooks/useNotificationMutes';
import { useUpdateNewDishSnooze } from '../../hooks/useProfile';
import { notify } from '../../services/notify';

interface SnoozeChooserSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** The chef whose new-dish posts triggered this notification. */
  listerId: string;
  /** Chef display name, for the "Snooze {name}" copy. */
  listerName?: string;
}

/**
 * Bottom-sheet chooser opened by the "Snooze" button on a new-dish activity
 * card. Two actions:
 *   - Snooze this chef  → per-chef NotificationMute (useSnoozeChef)
 *   - Snooze all new-dish alerts → global ProfileSettings toggle (useUpdateNewDishSnooze)
 *
 * Follows the app's `auto-sheet` IonModal convention (see HomeFeedSortPill).
 */
export const SnoozeChooserSheet: React.FC<SnoozeChooserSheetProps> = ({
  isOpen,
  onClose,
  listerId,
  listerName,
}) => {
  const { t } = useTranslation('activity');
  const activeProfileId = useProfileStore((s) => s.activeProfile?.id);

  const snoozeChef = useSnoozeChef();
  const snoozeAll = useUpdateNewDishSnooze();
  const pending = snoozeChef.isPending || snoozeAll.isPending;

  const name = listerName?.trim() || t('snooze.thisChef');

  const notifyError = () => notify.error(t('snooze.error'));

  const handleSnoozeChef = () => {
    if (!listerId || pending) return;
    snoozeChef.mutate(listerId, {
      onSuccess: () => {
        notify.success(t('snooze.toastChef', { name }));
        onClose();
      },
      onError: notifyError,
    });
  };

  const handleSnoozeAll = () => {
    if (!activeProfileId || pending) return;
    snoozeAll.mutate(
      { profileId: activeProfileId, snoozeNewDish: true },
      {
        onSuccess: () => {
          notify.success(t('snooze.toastAll'));
          onClose();
        },
        onError: notifyError,
      },
    );
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className="auto-sheet">
      <div className="ion-page">
        <div className="auto-sheet-content px-6 pt-4 pb-2">
          {/* No grab handle: breakpoint-less sheets get no drag gesture from
              Ionic, so a decorative bar just advertises a dismiss that isn't
              wired up. The X here and the button at the foot are the real ways
              out; backdrop tap still dismisses too. */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">
              {t('snooze.title')}
            </h3>
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              aria-label={t('common:actions.close')}
              className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground active:bg-muted disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <ul className="space-y-1">
            <li>
              <button
                type="button"
                onClick={handleSnoozeChef}
                disabled={pending || !listerId}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors active:bg-muted/60 disabled:opacity-50"
              >
                <UserX className="h-5 w-5 flex-shrink-0 text-foreground" />
                <span className="flex flex-col">
                  <span className="text-base font-medium text-foreground">
                    {t('snooze.thisChef')}
                  </span>
                  {listerName?.trim() && (
                    <span className="text-sm text-muted-foreground">
                      {t('snooze.thisChefDesc', { name: listerName.trim() })}
                    </span>
                  )}
                </span>
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={handleSnoozeAll}
                disabled={pending || !activeProfileId}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors active:bg-muted/60 disabled:opacity-50"
              >
                <BellOff className="h-5 w-5 flex-shrink-0 text-foreground" />
                <span className="flex flex-col">
                  <span className="text-base font-medium text-foreground">
                    {t('snooze.allNewDish')}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t('snooze.allNewDishDesc')}
                  </span>
                </span>
              </button>
            </li>
          </ul>

          {/* Both rows snooze something, so the way out is stated as its
              outcome rather than as a generic cancel. */}
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="mt-2 w-full py-3 text-center text-base font-medium text-muted-foreground active:opacity-60 disabled:opacity-50"
          >
            {t('snooze.keepAlerts')}
          </button>
        </div>
      </div>
    </IonModal>
  );
};
